import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Mail, Loader2, ArrowLeft } from "lucide-react";

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const email = (searchParams.get("email") || "").toLowerCase();

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate("/signup");
      return;
    }
    inputRefs.current[0]?.focus();
    // eslint-disable-next-line
  }, []);

  // Cooldown timer for "Resend"
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleChange = (i, value) => {
    const cleaned = (value || "").replace(/\D/g, "");
    // Paste 6-digit code into any field — distribute across all boxes.
    if (cleaned.length > 1) {
      const next = cleaned.slice(0, 6).split("");
      const padded = [...next, ...Array(6 - next.length).fill("")];
      setCode(padded);
      inputRefs.current[Math.min(next.length, 5)]?.focus();
      return;
    }
    const c = [...code];
    c[i] = cleaned;
    setCode(c);
    if (cleaned && i < 5) inputRefs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    } else if (e.key === "ArrowLeft" && i > 0) {
      inputRefs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < 5) {
      inputRefs.current[i + 1]?.focus();
    } else if (e.key === "Enter") {
      submit();
    }
  };

  const submit = async () => {
    const full = code.join("");
    if (full.length !== 6) {
      toast.error(t("auth:verify.enter_all", "Ingresá los 6 dígitos"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-email", { email, code: full });
      localStorage.setItem("nexa_token", data.token);
      setUser?.(data.user);
      toast.success(t("auth:verify.success", "¡Email verificado!"));
      navigate(data.user?.context_profile ? "/workspace" : "/onboarding");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("auth:verify.failed", "Verificación fallida"));
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success(t("auth:verify.resent", "Nuevo código enviado"));
      setCooldown(60);
    } catch (e) {
      const detail = e.response?.data?.detail || t("auth:verify.resend_failed", "No pudimos reenviar el código");
      toast.error(detail);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/signup" className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition flex items-center gap-1.5 mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> {t("auth:verify.back_to_signup", "Volver al registro")}
        </Link>

        <div className="border border-border bg-card rounded-sm p-8" data-testid="verify-email-card">
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-2">[ {t("auth:verify.eyebrow", "verificación")} ]</div>
          <h1 className="font-display font-extrabold tracking-tight text-2xl flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" /> {t("auth:verify.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {t("auth:verify.subtitle", { email })}
          </p>

          <div className="mt-6 flex gap-2 justify-center" data-testid="otp-boxes">
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                inputMode="numeric"
                maxLength={6}
                aria-label={t("auth:verify.digit_aria", { n: i + 1, defaultValue: "Dígito {{n}}" })}
                className="h-14 w-12 border border-border rounded-sm text-center text-xl font-mono font-bold bg-background focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                data-testid={`otp-input-${i}`}
              />
            ))}
          </div>

          <Button onClick={submit} disabled={busy || code.join("").length !== 6} className="w-full rounded-sm mt-6" data-testid="verify-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            {busy ? t("auth:verify.verifying", "Verificando…") : t("auth:verify.submit")}
          </Button>

          <div className="mt-4 text-center text-xs font-mono text-muted-foreground">
            {t("auth:verify.didnt_get", "¿No te llegó?")}{" "}
            {cooldown > 0 ? (
              <span data-testid="resend-cooldown">{t("auth:verify.resend_in", { s: cooldown, defaultValue: "Reenviar en {{s}}s" })}</span>
            ) : (
              <button
                onClick={resend}
                disabled={resending}
                className="text-primary hover:underline"
                data-testid="resend-button"
              >
                {resending ? t("auth:verify.sending", "Enviando…") : t("auth:verify.resend")}
              </button>
            )}
          </div>
          <div className="mt-2 text-center text-xs font-mono">
            <Link to="/signup" className="text-muted-foreground hover:text-foreground transition" data-testid="use-different-email">
              {t("auth:verify.use_different", "Usar otro email →")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
